import {useState, useEffect, React} from "react";
import BasicModal from "../../../layout/BasicModal";
import {
    TextField,
    Typography,
    Grid,
    Button,
    RadioGroup,
    Radio,
    FormControlLabel,
    Checkbox,
    FormLabel,
    FormGroup,
    FormControl,
    FormHelperText,
} from "@mui/material";
import BasicSlider from "../../../layout/BasicSlider";
import {successNote, toastErrorNotify} from "../../../helpers/toastNotify";
import Loading from "../../../layout/Loading";
import * as Yup from "yup";
import {useCheckAvailableTemplatesQuery, useAddTemplateMutation} from "../../../services/Template/templateSlice";
import { getModuleSupportFlags} from "../../../utils/moduleSupportUtils";
import {modulePackageName} from "../../..//utils/displayUtils";
import {MODULE_TYPE_LIST, resolveModuleType} from "../../../config/moduleTypes";

const testMark = [
    {
        value: 0,
        label: "0%",
    },
    {
        value: 100,
        label: "100%",
    },
];

const TemplatecreationModal = ({open, onClose, getTemplates}) => {
    const [patientValue, setPatientValue] = useState(1);
    const [approvalPercentage, setApprovalPercentage] = useState(0);
    const [testValue, setTestValue] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [radioValue, setRadioValue] = useState("");
    const [loading, setLoading] = useState(false);
    const [templateUrl, setTemplateUrl] = useState(null);
    const [generalSuccess, setGeneralSuccess] = useState(0);
    const [teoriValue, setTeoriValue] = useState(false);
    const [treningValue, setTreningValue] = useState(false);
    const [radioError, setRadioError] = useState(false);
    const [helperText, setHelperText] = useState("");
    const [checkBoxText, setCheckBoxText] = useState("");
    const [validationErrors, setValidationErrors] = useState({});
    const [getCheckboxTeoriLocked, setCheckboxTeoriLocked] = useState(false);
    const [getCheckboxTestLocked, setCheckboxTestLocked] = useState(false);
    const [getCheckboxTrainingLocked, setCheckboxTrainingLocked] = useState(false);

    const {data: availableTemplates,} = useCheckAvailableTemplatesQuery();
    const [addTemplate, {isLoading}] = useAddTemplateMutation();
    const { supportsTrainingPercentage} = getModuleSupportFlags(radioValue);

    // ! Validation Check
    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Malens navn er påkrevet"),
    });

    // ! Handle Radio Button and Input Change

    // Was a 5-way if/else (one branch per module type) hand-setting the add-URL plus 3
    // checkbox-lock flags. Now a single lookup into the module-type registry — adding a 6th
    // type needs zero changes here, just a new entry in config/moduleTypes.js.
    const handleRadioChange = (e) => {
        const value = e.target.value;
        setRadioValue(value);
        const type = resolveModuleType(value);

        setHelperText("");
        setRadioError(false);

        if (!type) {
            console.warn(`TemplateCreationModal: unrecognized module type "${value}".`);
            setCheckboxTeoriLocked(false);
            setCheckboxTestLocked(false);
            setCheckboxTrainingLocked(false);
            setTemplateUrl(null);
            return;
        }

        setCheckboxTeoriLocked(type.checkboxLocks.teori);
        setCheckboxTestLocked(type.checkboxLocks.test);
        setCheckboxTrainingLocked(type.checkboxLocks.trening);
        setTemplateUrl(type.endpoints.add);

        if (type.forcedCheckboxValues) {
            setCheckBoxStates({
                teori: type.forcedCheckboxValues.teori ?? teoriValue,
                test: type.forcedCheckboxValues.test ?? testValue,
                trening: type.forcedCheckboxValues.trening ?? treningValue,
            });
        }
    };

    const handleInputChange = (e) => {
        setTemplateName(e.target.value);
    };

    // ! Handle Sliders

    const handlePasientChange = (e) => {
        setPatientValue(e.target.value);
    };

    const handleTestChange = (e) => {
        setApprovalPercentage(e.target.value);
    };

    const handleGeneralSuccessChange = (e) => {
        setGeneralSuccess(e.target.value);
    };

    // ! Handle Checkboxes

    const handleCheckboxChange1 = (e) => {
        console.log(e);
        setTeoriValue(e.target.checked);
    };

    const handleCheckboxChange2 = (e) => {
        setTestValue(e.target.checked);
    };

    const handleCheckboxChange3 = (e) => {
        setTreningValue(e.target.checked);
    };

    function setCheckBoxStates({ teori, test, trening }) {
        setTeoriValue(teori);
        setTestValue(test);
        setTreningValue(trening);
        validateCheckBoxes(teori, test, trening);
    }


    // ! Add Template API Call

    async function handleClick(e) {
        e.preventDefault();
        setLoading(true);

        validateRadioValue(radioValue);
        validateCheckBoxes(teoriValue, testValue, treningValue);

        const initialValues = {
            name: templateName,
        };

        const params = {
            name: templateName,
            detail: "string",
            testApprovalPercentage: `${approvalPercentage}`,
            generalSuccessPercentage: `${generalSuccess}`,
            patientCount: `${patientValue}`,
            isActive: true,
            test: testValue,
            teori: teoriValue,
            training: treningValue,
        };

        try {
            await validationSchema.validate(initialValues, {abortEarly: false});
            const response = await addTemplate({templateUrl, params});

            handleApiResponse(response);

        } catch (err) {
            handleApiError(err);
        } finally {
            setLoading(false);
        }
    }

    function validateRadioValue(value) {
        const message = value === "" ? "Vennligst velg et alternativ" : "";
        setHelperText(message);
    }

    function validateCheckBoxes(teori, test, trening) {
        const message = !(teori || test || trening) ? "Vennligst velg minumum ett alternativ" : "";
        setCheckBoxText(message);
    }

    function handleApiResponse(response) {
        if (response?.data && response.data.statusCode === 200) {
            successNote("Ny mal lagt til");
            onClose(onClose);
            resetFormValues();
        } else if (response?.error) {
            toastErrorNotify(response.error.data.message);
        }
    }

    function resetFormValues() {
        setTeoriValue(false);
        setTestValue(false);
        setTreningValue(false);
        setCheckboxTeoriLocked(false)
        setCheckboxTestLocked(false)
        setCheckboxTrainingLocked(false)
        setRadioValue('');
    }

    function handleApiError(err) {
        if (err?.inner && Array.isArray(err.inner)) {
            const errorMap = {};
            err.inner.forEach((error) => {
                errorMap[error.path] = error.message;
            });
            setValidationErrors(errorMap);
        }

        const {response} = err;

        if (!response) {
            toastErrorNotify("Ingen serverrespons");
        } else if (response.data) {
            const {data} = response;
            if (data.validationErrors && data.validationErrors.length > 0) {
                toastErrorNotify(data.validationErrors[0]);
            } else if (data.message) {
                toastErrorNotify(data.message);
            } else {
                toastErrorNotify("Feilet");
            }
        }
    }

    // Inside your component
    useEffect(() => {
        if(availableTemplates === undefined) {
            return;
        }
        console.log("Available templates:", availableTemplates);
    }, [availableTemplates]);

    const activeType = resolveModuleType(radioValue);

    const getContent = () => (
        <Grid
            container
            rowSpacing={3}
            columnSpacing={{xs: 1, sm: 2, md: 3}}
            maxWidth="md"
        >
            {/* // ! Radio button for helse choice */}
            {!availableTemplates?.data.fysisk && !availableTemplates?.data.mental && (
                <Grid item xs={12}>
                    <FormHelperText sx={{color: "red"}}>
                        {" "}
                        Mental eller fysisk malene deres er ikke tilgjengelige for
                        øyeblikket
                    </FormHelperText>
                </Grid>
            )}
            <Grid item xs={12}>
                <FormControl>
                    <FormLabel id="demo-radio-buttons-group-label">
                        Opprett mal for
                    </FormLabel>
                    <RadioGroup
                        aria-labelledby="demo-radio-buttons-group-label"
                        defaultValue="fysisk"
                        name="radio-buttons-group"
                        row
                        onChange={handleRadioChange}
                        value={radioValue}
                    >
                        {/* Used to be 5 hand-written <FormControlLabel> blocks, one per module
                            type, each gated on its own hardcoded (and inconsistently-cased —
                            see MODULE_ARCHITECTURE_PLAN.md §6) key of availableTemplates?.data.
                            Now a single loop over the registry; a 6th type needs zero JSX
                            changes here. */}
                        {MODULE_TYPE_LIST.map((type) => (
                            availableTemplates?.data?.[type.availabilityKey] && (
                                <FormControlLabel
                                    key={type.key}
                                    value={type.key}
                                    control={<Radio/>}
                                    label={modulePackageName(type.key)}
                                />
                            )
                        ))}
                    </RadioGroup>
                    <FormHelperText sx={{color: "red"}}>{helperText}</FormHelperText>
                </FormControl>
            </Grid>

            {/* // ! Input for template name*/}

            <Grid item xs={12}>
                <TextField
                    required
                    fullWidth
                    id="outlined-required"
                    label="Malens navn"
                    onChange={handleInputChange}
                    error={Boolean(validationErrors.name)}
                    helperText={validationErrors.name}
                />
            </Grid>

            {/* // ! Checkbox for test/teori/trening */}
            <Grid item xs={12}>
                <FormLabel component="legend"> Vennligst velg minst én</FormLabel>
                <FormGroup aria-label="position" row>
                    <FormControlLabel
                        label="Teori"
                        control={
                        <Checkbox checked={teoriValue} onChange={handleCheckboxChange1} disabled={getCheckboxTeoriLocked} />}
                    />
                    <FormControlLabel
                        label="Test"
                        control={<Checkbox checked={testValue} onChange={handleCheckboxChange2} disabled={getCheckboxTestLocked} />}
                    />
                    <FormControlLabel
                        label="Trening"
                        control={<Checkbox checked={treningValue} onChange={handleCheckboxChange3} disabled={getCheckboxTrainingLocked} />}
                    />
                </FormGroup>
                {/* {error && ( */}
                <FormHelperText error>{checkBoxText}</FormHelperText>
                {/* )} */}
            </Grid>

            {/* // ! Slider 1 */}

            {activeType?.key === "fysisk" && (
                <Grid item xs={12}>
                    <Typography>Vennligst velg antall pasienter</Typography>
                    <BasicSlider
                        min={1}
                        maks={10}
                        step={1}
                        marks
                        onChange={handlePasientChange}
                    />
                </Grid>
            )}

            {((treningValue && supportsTrainingPercentage) || testValue) && (
                <Grid item xs={12}>
                    <Typography>Krav til riktige besvarelser, i prosent</Typography>
                </Grid>
            )
            }
            {(treningValue && !supportsTrainingPercentage) && (
                <Grid item xs={12}>
                    <FormLabel>Trening</FormLabel>
                    <Typography sx = {{fontStyle: "italic"}}>Besvarelsen blir enten godkjent eller ikke godkjent.</Typography>
                </Grid>
            )}

            {/* // ! Slider 2 */}

            {testValue && (
                <Grid item xs={12}>
                    <FormLabel>Test</FormLabel>
                    <BasicSlider
                        min={0}
                        maks={100}
                        step={5}
                        marks={testMark}
                        onChange={handleTestChange}
                    />
                </Grid>
            )}

            {/* // ! Slider 3 */}


            {  (treningValue && supportsTrainingPercentage) && (
                <Grid item xs={12}>
                    <FormLabel>Trening</FormLabel>
                    <BasicSlider
                        min={0}
                        maks={100}
                        step={5}
                        marks={testMark}
                        onChange={handleGeneralSuccessChange}
                    />
                </Grid>
            )}

            {/* // ! Submit Button */}

            <Grid item xs={12}>
                <Button
                    variant="contained"
                    fullWidth
                    className="PrimaryButton"
                    onClick={handleClick}
                    disabled={loading}
                >
                    {loading ? <Loading color={"#fff"} size={25}/> : "Opprett"}
                </Button>
            </Grid>
        </Grid>
    );

    return (
        <BasicModal
            open={open}
            onClose={onClose}
            title="Opprett ny mal"
            content={getContent()}
        />
    );
};

export default TemplatecreationModal;
